import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { ReactElement, ReactNode, useEffect } from "react";
import { Link, useNavigate } from "react-router";

type Response = TRouteWithRedirect<unknown, string>;
type Props = Readonly<{
  response?: Response;
  clientSideError?: true;
}>;

type ErrorMessage = { pageTitle: string; title: string; lead: ReactNode };

const SERVER_ERROR_MESSAGE = {
  pageTitle: "Unexpected Error",
  title: "Oops, something went wrong.",
  lead: (
    <>
      <p className="font-sans font-24">
        <strong>Dear Reader,</strong>
      </p>
      <p className="font-sans font-18">
        We are experiencing technical difficulties and we are working to restore
        our services as soon as possible.
        <br />
        In the meantime, get the latest news from:
        <br />
        <br />
        <Link
          to="https://www.facebook.com/tablasph/"
          target="_blank"
          className="text-red-100"
        >
          - Tabla Facebook{" "}
        </Link>
        <br />
        <Link
          to="https://www.instagram.com/tabla_sph/"
          target="_blank"
          className="text-red-100"
        >
          - Tabla Instagram{" "}
        </Link>
        <br />
        <Link
          to="https://www.tiktok.com/@tablatoks"
          target="_blank"
          className="text-red-100"
        >
          - Tabla Tiktok{" "}
        </Link>
        <br />
        <br />
        For urgent assistance, please contact our Customer Experience team at{" "}
        <Link to="callto:63883838" className="text-red-100">
          6388-3838
        </Link>
        <br />
        Our hotline is open from 8.30am to 6pm on Monday to Friday and from
        8.30am to 12noon on Saturday and Sunday. We are closed on public
        holidays.
      </p>
    </>
  ),
};

const textForStatusCode: Partial<
  Record<
    Response["statusCode"] | ResponseType | "clientSideError",
    ErrorMessage
  >
> = {
  [ResponseType.CLIENT_ERROR]: {
    pageTitle: "Bad Request",
    title: "We are sorry, the page you are looking for cannot be served.",
    lead: "There is a problem with your request.",
  },
  404: {
    pageTitle: "Page not found",
    title: "Sorry, the page you visited no longer exists.",
    lead: (
      <>
        Please visit our{" "}
        <span>
          <a href="/" className="text-red-100">
            Home Page
          </a>
        </span>
      </>
    ),
  },
  [ResponseType.REDIRECT]: {
    pageTitle: "Redirect",
    title: "You are being redirected.",
    lead: undefined,
  },
  [ResponseType.SERVER_ERROR]: SERVER_ERROR_MESSAGE,
  503: {
    pageTitle: "Service Unavailable",
    title: "Oops, something took too long.",
    lead: "Try refreshing this page after 30 seconds or contact us if the problem persists.",
  },
  clientSideError: {
    pageTitle: "Unexpected Error",
    title: "Oops, something went wrong.",
    lead: "Try refreshing this page, updating your browser or contact us if the problem persists.",
  },
};

export default function ErrorPage({
  response,
  clientSideError,
}: Props): ReactElement {
  const text: ErrorMessage =
    (clientSideError
      ? textForStatusCode.clientSideError
      : ((response?.statusCode !== undefined &&
          textForStatusCode[response.statusCode]) ??
        (response?.type !== undefined && textForStatusCode[response.type]))) ||
    SERVER_ERROR_MESSAGE;
  const navigate = useNavigate();

  useEffect(() => {
    if (response?.type === ResponseType.REDIRECT) {
      void navigate(response.target);
    }
  }, [response, navigate]);

  return (
    <>
      <div className="flex flex-col items-center mt-[150px]">
        <h1 className="text-md lg:text-lg">{text.title}</h1>
        <p className="text-md lg:text-lg">
          {response?.type === ResponseType.REDIRECT ? (
            <a href={response.target}>
              <u>Please click here if your browser does not redirect you.</u>
            </a>
          ) : (
            text.lead
          )}
        </p>
        {response ? (
          <p>
            {ResponseType[response.type]} &middot; {response.statusCode}
          </p>
        ) : null}
      </div>
    </>
  );
}
